import React, { useEffect, useState, useCallback } from "react";
import { trackFeatureAction } from "../../../analytics/googleAnalytics";
import { useDispatch, useSelector } from "react-redux";
import { fetchARReport, downloadARReport } from "../../../redux/slice/arReportSlice";
import { HashLoader } from "react-spinners";
import { Card, Button, Input, Select, Space, Alert, Empty } from "antd";
import { StyledTable, StyledButton } from "../components/styleComp";
import { useAuth } from "../../../context/AuthContext";

const { Option } = Select;

// Branch AR / Credit AR report. reportType: "branch" | "credit".
// Search and paging run on the server; the backend also limits rows to the user's branches.
const ARReport = ({ reportType }) => {
  const label = reportType === "credit" ? "Credit AR Report" : "Branch AR Report";

  const { authState } = useAuth();
  const dispatch = useDispatch();
  const jwtBearer = authState?.token;

  const [buttonLoading, setButtonLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 7 });
  const [selectedColumn, setSelectedColumn] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({});

  useEffect(() => {
    if (jwtBearer) {
      dispatch(
        fetchARReport({
          jwtBearer,
          reportType,
          page: pagination.page,
          pageSize: pagination.pageSize,
          filters,
        })
      );
    }
  }, [jwtBearer, reportType, pagination, filters, dispatch]);

  const {
    data = [],
    totalCount,
    loading,
    error,
  } = useSelector((state) => state?.arReport?.[reportType] || {});

  const defineColumns = useCallback((rows) => {
    if (!rows || rows?.length === 0) return [];
    const keys = Object.keys(rows[0]);
    return keys.map((key) => ({
      title: <strong style={{ fontSize: "18px" }}>{key.replace(/_/g, " ").toUpperCase()}</strong>,
      dataIndex: key,
      key: key,
      render: (text) => <span style={{ fontSize: "16px" }}>{text}</span>,
    }));
  }, []);

  const hasSearched = Object.keys(filters).length > 0;

  const handleFilter = () => {
    if (!selectedColumn) return;
    trackFeatureAction("Reports", label, "search");
    setFilters({ [selectedColumn.toLowerCase()]: searchInput.trim() });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleFilter();
    }
  };

  const handleReset = () => {
    trackFeatureAction("Reports", label, "reset");
    setSelectedColumn("");
    setSearchInput("");
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const paginationConfig = {
    current: pagination.page,
    pageSize: pagination.pageSize,
    total: totalCount,
    showTotal: (total, [start, end]) => `${start}-${end} of ${total} items`,
    onChange: (page, pageSize) => setPagination({ page, pageSize }),
  };

  const handleAPIDownload = () => {
    trackFeatureAction("Reports", label, "download");
    setButtonLoading(true);
    dispatch(downloadARReport({ jwtBearer, reportType }))
      .finally(() => setButtonLoading(false));
  };

  const columns = defineColumns(data);

  return (
    <Card
      title={<span style={{ fontWeight: 600, fontSize: "24px", fontFamily: "poppins-font, sans-serif" }}>{label.toUpperCase()}</span>}
      bordered
      extra={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <StyledButton
            type="primary"
            onClick={handleAPIDownload}
            loading={buttonLoading}
          >
            Download Report
          </StyledButton>
        </div>
      }
    >
      <Space style={{ marginBottom: 16, marginLeft: 2 }}>
        <Select
          placeholder="Select Column"
          style={{ width: 200, height: 32, lineHeight: "32px" }}
          value={selectedColumn || undefined}
          onChange={(value) => setSelectedColumn(value)}
        >
          {columns?.map((col) => (
            <Option key={col?.key} value={col?.dataIndex}>
              {col?.title?.props?.children}
            </Option>
          ))}
        </Select>
        <Input
          placeholder="Value"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{ width: 150, height: 32, lineHeight: "32px" }}
          disabled={!selectedColumn}
        />
        <Button
          type="primary"
          onClick={handleFilter}
          style={{ height: 32, lineHeight: "32px" }}
        >
          Search
        </Button>
        {hasSearched && (
          <Button
            onClick={handleReset}
            style={{ width: 70, height: 32, lineHeight: "32px" }}
          >
            Reset
          </Button>
        )}
      </Space>

      {loading ? (
        <div className="spinner-container">
          <HashLoader color="#f57171" loading={true} />
        </div>
      ) : error ? (
        <Alert message={error} type="error" />
      ) : (
        <StyledTable
          columns={columns}
          dataSource={data}
          rowKey="APPLICATION_ID"
          pagination={paginationConfig}
          locale={{ emptyText: <Empty description="No data available" /> }}
        />
      )}
    </Card>
  );
};

export default ARReport;
